import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  Trash2,
  UserX,
  UserCheck,
  X,
  Shield,
  User,
  Mail,
  Phone,
} from "lucide-react";

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  type = "delete", // delete, activate, deactivate, update
  title,
  message,
  teamMember,
  isLoading = false,
  confirmText,
  cancelText = "Cancel",
  icon: CustomIcon,
}) => {
  if (!isOpen) return null;

  const getModalConfig = () => {
    switch (type) {
      case "delete":
        return {
          icon: Trash2,
          iconColor: "text-red-500",
          iconBg: "bg-red-100",
          title: "Delete Team Member",
          message:
            "Are you sure you want to delete this team member? This action cannot be undone.",
          confirmText: "Delete Member",
          confirmColor: "bg-red-500 hover:bg-red-600",
          borderColor: "border-red-200",
        };
      case "activate":
        return {
          icon: UserCheck,
          iconColor: "text-green-500",
          iconBg: "bg-green-100",
          title: "Activate Team Member",
          message:
            "Are you sure you want to activate this team member? They will regain access to the system.",
          confirmText: "Activate Member",
          confirmColor: "bg-green-500 hover:bg-green-600",
          borderColor: "border-green-200",
        };
      case "deactivate":
        return {
          icon: UserX,
          iconColor: "text-orange-500",
          iconBg: "bg-orange-100",
          title: "Deactivate Team Member",
          message:
            "Are you sure you want to deactivate this team member? They will lose access to the system.",
          confirmText: "Deactivate Member",
          confirmColor: "bg-orange-500 hover:bg-orange-600",
          borderColor: "border-orange-200",
        };
      case "update":
        return {
          icon: Shield,
          iconColor: "text-blue-500",
          iconBg: "bg-blue-100",
          title: "Update Team Member",
          message:
            "Are you sure you want to update this team member's information?",
          confirmText: "Update Member",
          confirmColor: "bg-blue-500 hover:bg-blue-600",
          borderColor: "border-blue-200",
        };
      default:
        return {
          icon: AlertTriangle,
          iconColor: "text-gray-500",
          iconBg: "bg-gray-100",
          title: title || "Confirm Action",
          message: message || "Are you sure you want to proceed?",
          confirmText: confirmText || "Confirm",
          confirmColor: "bg-gray-500 hover:bg-gray-600",
          borderColor: "border-gray-200",
        };
    }
  };

  const config = getModalConfig();
  const IconComponent = CustomIcon || config.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed right-0 bottom-0 left-0 -top-10 z-50 backdrop-blur-sm bg-black/50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="flex fixed top-0 right-0 bottom-0 left-0 z-50 justify-center items-center p-4"
          >
            <div className="w-full max-w-sm">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className={`relative bg-white rounded-xl shadow-xl border ${config.borderColor} overflow-hidden`}
              >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-100">
                  <div className="flex gap-2 items-center">
                    <div className={`p-1.5 rounded-lg ${config.iconBg}`}>
                      <IconComponent
                        className={`w-5 h-5 ${config.iconColor}`}
                      />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      {config.title}
                    </h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4" />
                  </motion.button>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="mb-4 text-sm leading-relaxed text-gray-600">
                    {config.message}
                  </p>

                  {/* Compact Team Member Info */}
                  {teamMember && (
                    <div className="p-3 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex gap-2 items-center">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600 truncate">
                            {teamMember.fullName ||
                              teamMember.full_name ||
                              "N/A"}
                          </span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600 truncate">
                            {teamMember.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600 truncate">
                            {teamMember.phone || "N/A"}
                          </span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Shield className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600 truncate">
                            {teamMember.role || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Compact Warning for destructive actions */}
                  {type === "delete" && (
                    <div className="p-3 mb-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex gap-2 items-start">
                        <AlertTriangle className="mt-0.5 w-4 h-4 text-red-500 flex-shrink-0" />
                        <div>
                          <h4 className="mb-1 text-xs font-semibold text-red-800">
                            Warning
                          </h4>
                          <p className="text-xs text-red-700">
                            This action will permanently remove the team member
                            and all their associated data.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 p-4 bg-gray-50">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    disabled={isLoading}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelText}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onConfirm}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 ${config.confirmColor} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-3 h-3 rounded-full border-2 border-white animate-spin border-t-transparent" />
                        Processing...
                      </>
                    ) : (
                      config.confirmText
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
